'use client';

import { useState, useEffect } from 'react';
import { Globe } from 'lucide-react';

import { Button } from '@kit/ui/button';
import { currencyFromCountryCode } from '~/lib/currency';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@kit/ui/dropdown-menu';

// Currency configuration
const CURRENCIES = {
  USD: { label: 'USD ($)', symbol: '$', flag: '🇺🇸' },
  AUD: { label: 'AUD (A$)', symbol: 'A$', flag: '🇦🇺' },
} as const;

type Currency = keyof typeof CURRENCIES;

interface CurrencySelectorProps {
  onCurrencyChange?: (currency: Currency) => void;
  className?: string;
}

// Client-side fallback deliberately defaults to USD. The marketing page passes
// the hosting edge's trusted country header as the server-side initial value.
function detectUserCountry(): Currency {
  // Check environment variable first for testing
  if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_CURRENCY === 'AUD') {
    console.log('[Currency Detection] Environment variable set to AUD, using AUD');
    return 'AUD';
  }
  
  return currencyFromCountryCode(null);
}

export function readSavedCurrency(storage: Pick<Storage, 'getItem'> | undefined): Currency | null {
  try {
    const saved = storage?.getItem('leagen-currency-preference') as Currency | null;
    return saved && CURRENCIES[saved] ? saved : null;
  } catch {
    return null;
  }
}

export function resolveCurrencyPreference(
  initialCurrency: Currency,
  storage: Pick<Storage, 'getItem'> | undefined,
): Currency {
  return readSavedCurrency(storage) ?? initialCurrency;
}

function getBrowserStorage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

export function CurrencySelector({ onCurrencyChange, className }: CurrencySelectorProps) {
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>('USD');
  const [mounted, setMounted] = useState(false);

  // Single useEffect to handle mounting, detection, and saved preference loading
  useEffect(() => {
    setMounted(true);
    
    // Check localStorage first for saved preference
    const finalCurrency = resolveCurrencyPreference(
      detectUserCountry(),
      getBrowserStorage(),
    );
    
    setSelectedCurrency(finalCurrency);
    onCurrencyChange?.(finalCurrency);
  }, [onCurrencyChange]);

  const handleCurrencyChange = (currency: Currency) => {
    setSelectedCurrency(currency);
    onCurrencyChange?.(currency);
    
    // Store preference in localStorage
    try {
      localStorage.setItem('leagen-currency-preference', currency);
    } catch {
      // The selection remains active for this render even when storage is blocked.
    }
  };

  // Prevent hydration mismatch
  if (!mounted) {
    return (
      <Button variant="outline" size="sm" className={className} disabled>
        <Globe className="h-4 w-4 mr-2" />
        USD ($)
      </Button>
    );
  }

  const current = CURRENCIES[selectedCurrency];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={className}>
          <Globe className="h-4 w-4 mr-2" />
          <span className="hidden sm:inline">{current.flag}</span>
          <span className="ml-1">{current.label}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {Object.entries(CURRENCIES).map(([code, config]) => (
          <DropdownMenuItem
            key={code}
            onClick={() => handleCurrencyChange(code as Currency)}
            className={selectedCurrency === code ? 'bg-accent' : ''}
          >
            <span className="mr-2">{config.flag}</span>
            <span>{config.label}</span>
            {code === 'USD' && <span className="ml-auto text-xs text-muted-foreground">Global</span>}
            {code === 'AUD' && <span className="ml-auto text-xs text-muted-foreground">Australia</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Hook for using currency in components
export function useCurrency(initialCurrency: Currency = 'USD') {
  const [currency, setCurrency] = useState<Currency>(initialCurrency);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setCurrency(
      resolveCurrencyPreference(
        initialCurrency,
        getBrowserStorage(),
      ),
    );
  }, [initialCurrency]);

  return {
    currency: mounted ? currency : initialCurrency,
    setCurrency,
    config: CURRENCIES[currency],
    mounted,
  };
}

// Export currency types and config for use in other components
export type { Currency };
export { CURRENCIES };
